import { useEffect, useState } from "react";
import axios from "axios";
import { Lock, LogOut, CalendarDays, CheckCircle2, Inbox, Phone, Mail, Newspaper, Plus, Pencil, Trash2 } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { LogoMark } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKENS_KEY = "legalia_admin_tokens";

const STATUSES = ["in attesa", "confermato", "completato"];

function formatApiErrorDetail(detail) {
  if (detail == null) return "Qualcosa è andato storto: riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const formatDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

export default function Admin() {
  const [tokens, setTokens] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(TOKENS_KEY));
    } catch {
      return null;
    }
  });
  const [checking, setChecking] = useState(!!tokens);
  const [data, setData] = useState(null);
  const [loginError, setLoginError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("appuntamenti");

  const [newsList, setNewsList] = useState(null);
  const [newsForm, setNewsForm] = useState({ id: null, title: "", body: "", published: false });
  const [newsSaving, setNewsSaving] = useState(false);
  const [newsError, setNewsError] = useState("");

  const authedCall = async (fn) => {
    try {
      return await fn(tokens.access_token);
    } catch (e) {
      if (e?.response?.status === 401 && tokens?.refresh_token) {
        try {
          const r = await axios.post(`${API}/auth/refresh`, { refresh_token: tokens.refresh_token });
          const tk = { access_token: r.data.access_token, refresh_token: r.data.refresh_token };
          localStorage.setItem(TOKENS_KEY, JSON.stringify(tk));
          setTokens(tk);
          return await fn(tk.access_token);
        } catch {
          doLogout();
          throw e;
        }
      }
      throw e;
    }
  };

  const loadRequests = async (tk = tokens) => {
    const res = await axios.get(`${API}/admin/requests`, {
      headers: { Authorization: `Bearer ${tk.access_token}` },
    });
    setData(res.data);
  };

  const loadNews = async (tk = tokens) => {
    const res = await axios.get(`${API}/admin/news`, {
      headers: { Authorization: `Bearer ${tk.access_token}` },
    });
    setNewsList(res.data);
  };

  useEffect(() => {
    if (!tokens) return;
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${tokens.access_token}` } })
      .then(() => loadRequests(tokens))
      .catch(() => {
        localStorage.removeItem(TOKENS_KEY);
        setTokens(null);
      })
      .finally(() => setChecking(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab === "news" && tokens && newsList === null) {
      loadNews(tokens).catch(() => setNewsList([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const onLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, {
        email: e.target.email.value,
        password: e.target.password.value,
      });
      const tk = { access_token: res.data.access_token, refresh_token: res.data.refresh_token };
      localStorage.setItem(TOKENS_KEY, JSON.stringify(tk));
      setTokens(tk);
      await loadRequests(tk);
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const doLogout = () => {
    localStorage.removeItem(TOKENS_KEY);
    setTokens(null);
    setData(null);
    setNewsList(null);
  };

  const setStatus = async (id, status) => {
    try {
      await authedCall((tk) =>
        axios.patch(`${API}/admin/requests/${id}/status`, { status }, { headers: { Authorization: `Bearer ${tk}` } })
      );
      setData((prev) => ({
        ...prev,
        requests: prev.requests.map((r) => (r.id === id ? { ...r, status } : r)),
        stats: {
          ...prev.stats,
          confirmed: prev.requests.filter((r) => (r.id === id ? status === "confermato" : r.status === "confermato")).length,
        },
      }));
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveNews = async (e) => {
    e.preventDefault();
    setNewsSaving(true);
    setNewsError("");
    try {
      await authedCall((tk) =>
        newsForm.id
          ? axios.patch(
              `${API}/admin/news/${newsForm.id}`,
              { title: newsForm.title, body: newsForm.body, published: newsForm.published },
              { headers: { Authorization: `Bearer ${tk}` } }
            )
          : axios.post(`${API}/admin/news`, newsForm, { headers: { Authorization: `Bearer ${tk}` } })
      );
      setNewsForm({ id: null, title: "", body: "", published: false });
      await loadNews();
    } catch (err) {
      setNewsError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setNewsSaving(false);
    }
  };

  const deleteNews = async (id) => {
    if (!window.confirm("Eliminare definitivamente questo aggiornamento?")) return;
    try {
      await authedCall((tk) => axios.delete(`${API}/admin/news/${id}`, { headers: { Authorization: `Bearer ${tk}` } }));
      if (newsForm.id === id) setNewsForm({ id: null, title: "", body: "", published: false });
      await loadNews();
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const inputCls =
    "w-full border border-hairline bg-white px-4 py-3.5 text-sm text-ink placeholder:text-muted-ink/50 transition-colors duration-300 focus:border-gold focus:outline-none";

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-alabaster pt-9">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-ink">Caricamento…</p>
      </div>
    );
  }

  if (!tokens) {
    return (
      <div data-testid="admin-login" className="flex min-h-screen items-center justify-center bg-alabaster px-6 pb-20 pt-32">
        <Reveal className="w-full max-w-md">
          <form onSubmit={onLogin} className="border border-hairline bg-white p-10">
            <div className="flex flex-col items-center text-center">
              <LogoMark className="h-14" />
              <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.25em] text-gold-dark">Area riservata</p>
              <h1 className="mt-2 font-serif text-3xl font-semibold text-navy">Gestione Studio</h1>
              <p className="mt-2 text-sm text-muted-ink">Accedi con le credenziali dello studio.</p>
            </div>
            <div className="mt-8 space-y-4">
              <div>
                <label htmlFor="admin-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-muted-ink">
                  Email
                </label>
                <input id="admin-email" data-testid="admin-login-email" name="email" type="email" required className={inputCls} placeholder="email dello studio" />
              </div>
              <div>
                <label htmlFor="admin-password" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-muted-ink">
                  Password
                </label>
                <input id="admin-password" data-testid="admin-login-password" name="password" type="password" required className={inputCls} placeholder="••••••••" />
              </div>
              {loginError && (
                <p data-testid="admin-login-error" className="border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-600">
                  {loginError}
                </p>
              )}
              <button
                data-testid="admin-login-submit"
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 bg-gold px-6 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-navy transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                <Lock size={13} strokeWidth={1.5} />
                {loading ? "Accesso…" : "Accedi"}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    );
  }

  const { stats, requests } = data || { stats: { total: 0, upcoming: 0, confirmed: 0 }, requests: [] };

  const statCards = [
    { id: "total", label: "Richieste totali", value: stats.total, icon: Inbox },
    { id: "upcoming", label: "Appuntamenti in arrivo", value: stats.upcoming, icon: CalendarDays },
    { id: "confirmed", label: "Confermati", value: stats.confirmed, icon: CheckCircle2 },
  ];

  const TabButton = ({ id, label, icon: Icon }) => (
    <button
      data-testid={`admin-tab-${id}`}
      onClick={() => setTab(id)}
      className={`flex items-center gap-2 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
        tab === id ? "border-b-2 border-gold text-navy" : "border-b-2 border-transparent text-muted-ink hover:text-navy"
      }`}
    >
      <Icon size={13} strokeWidth={1.5} />
      {label}
    </button>
  );

  return (
    <div data-testid="admin-panel" className="min-h-screen bg-alabaster pb-24 pt-32 lg:pt-40">
      <div className="mx-auto max-w-5xl px-6 lg:px-12">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <Overline>Area riservata</Overline>
              <h1 className="mt-3 font-serif text-4xl font-medium tracking-tight text-navy sm:text-5xl">
                Gestione Studio
              </h1>
            </div>
            <button
              data-testid="admin-logout"
              onClick={doLogout}
              className="flex items-center gap-2 border border-hairline px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-navy transition-colors hover:border-gold hover:text-gold-dark"
            >
              <LogOut size={13} strokeWidth={1.5} />
              Esci
            </button>
          </div>
        </Reveal>

        <div className="mt-8 flex gap-2 border-b border-hairline">
          <TabButton id="appuntamenti" label="Appuntamenti" icon={CalendarDays} />
          <TabButton id="news" label="News" icon={Newspaper} />
        </div>

        {tab === "appuntamenti" && (
          <>
            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {statCards.map((s, i) => {
                const Icon = s.icon;
                return (
                  <Reveal key={s.id} delay={0.05 * i}>
                    <div data-testid={`admin-stat-${s.id}`} className="flex items-center gap-4 border border-hairline bg-white p-6">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-hairline text-gold-dark">
                        <Icon size={18} strokeWidth={1.25} />
                      </span>
                      <div>
                        <p className="font-serif text-3xl font-semibold text-navy">{s.value}</p>
                        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-ink">{s.label}</p>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>

            <div data-testid="admin-requests-list" className="mt-8 space-y-4">
              {requests.length === 0 && (
                <div className="border border-hairline bg-white p-10 text-center">
                  <p className="font-serif text-xl text-navy">Nessuna richiesta ricevuta</p>
                  <p className="mt-2 text-sm text-muted-ink">Le richieste inviate dal form del sito compariranno qui.</p>
                </div>
              )}
              {requests.map((r, i) => (
                <Reveal key={r.id} delay={0.03 * Math.min(i, 8)}>
                  <div data-testid={`request-card-${r.id}`} className="border border-hairline bg-white p-6">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <span className="flex h-12 w-16 shrink-0 flex-col items-center justify-center bg-navy text-center leading-tight">
                          <span className="font-serif text-sm font-semibold text-gold">{formatDate(r.data_appuntamento)}</span>
                          <span className="mt-0.5 font-mono text-[9px] text-alabaster/70">{r.ora_appuntamento || "--:--"}</span>
                        </span>
                        <div>
                          <h3 className="font-serif text-xl font-semibold text-navy">{r.nome}</h3>
                          <p className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-gold-dark">{r.area}</p>
                        </div>
                      </div>
                      <label className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.2em] text-muted-ink">
                        Stato
                        <select
                          data-testid={`status-select-${r.id}`}
                          value={r.status || "in attesa"}
                          onChange={(e) => setStatus(r.id, e.target.value)}
                          className="border border-hairline bg-alabaster px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-navy focus:border-gold focus:outline-none"
                        >
                          {STATUSES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <p className="mt-4 text-sm leading-relaxed text-muted-ink">{r.messaggio}</p>
                    <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 border-t border-hairline pt-4 font-mono text-[10px] tracking-[0.1em] text-muted-ink">
                      <a href={`mailto:${r.email}`} className="flex items-center gap-1.5 hover:text-gold-dark">
                        <Mail size={11} strokeWidth={1.5} />
                        {r.email}
                      </a>
                      {r.telefono && (
                        <a href={`tel:${r.telefono.replace(/\s/g, "")}`} className="flex items-center gap-1.5 hover:text-gold-dark">
                          <Phone size={11} strokeWidth={1.5} />
                          {r.telefono}
                        </a>
                      )}
                      <span>{new Date(r.timestamp).toLocaleString("it-IT")}</span>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </>
        )}

        {tab === "news" && (
          <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.2fr]">
            <div>
              <Reveal>
                <form data-testid="admin-news-form" onSubmit={saveNews} className="border border-hairline bg-white p-6">
                  <h3 className="flex items-center gap-2 font-serif text-xl font-semibold text-navy">
                    <Newspaper size={17} strokeWidth={1.25} className="text-gold-dark" />
                    {newsForm.id ? "Modifica aggiornamento" : "Nuovo aggiornamento"}
                  </h3>
                  <div className="mt-5 space-y-4">
                    <div>
                      <label htmlFor="news-title" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-muted-ink">
                        Titolo
                      </label>
                      <input
                        id="news-title"
                        data-testid="admin-news-title"
                        type="text"
                        required
                        value={newsForm.title}
                        onChange={(e) => setNewsForm((f) => ({ ...f, title: e.target.value }))}
                        className={inputCls}
                        placeholder="Titolo dell'aggiornamento"
                      />
                    </div>
                    <div>
                      <label htmlFor="news-body" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-muted-ink">
                        Testo
                      </label>
                      <textarea
                        id="news-body"
                        data-testid="admin-news-body"
                        required
                        rows={9}
                        value={newsForm.body}
                        onChange={(e) => setNewsForm((f) => ({ ...f, body: e.target.value }))}
                        className={`${inputCls} resize-none`}
                        placeholder="Scrivi l'aggiornamento legale…"
                      />
                    </div>
                    <label className="flex cursor-pointer items-center gap-3">
                      <input
                        data-testid="admin-news-published"
                        type="checkbox"
                        checked={newsForm.published}
                        onChange={(e) => setNewsForm((f) => ({ ...f, published: e.target.checked }))}
                        className="h-4 w-4 cursor-pointer appearance-none border border-navy/40 bg-transparent transition-colors checked:border-gold checked:bg-gold"
                      />
                      <span className="text-xs text-muted-ink">Pubblica sul sito (visibile a tutti)</span>
                    </label>
                    {newsError && <p className="border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-600">{newsError}</p>}
                    <div className="flex gap-3">
                      <button
                        data-testid="admin-news-save"
                        type="submit"
                        disabled={newsSaving}
                        className="flex flex-1 items-center justify-center gap-2 bg-gold px-6 py-3.5 font-mono text-[10px] uppercase tracking-[0.2em] text-navy transition-opacity hover:opacity-90 disabled:opacity-60"
                      >
                        <Plus size={13} strokeWidth={1.5} />
                        {newsSaving ? "Salvataggio…" : newsForm.id ? "Aggiorna" : "Pubblica"}
                      </button>
                      {newsForm.id && (
                        <button
                          type="button"
                          data-testid="admin-news-cancel"
                          onClick={() => setNewsForm({ id: null, title: "", body: "", published: false })}
                          className="border border-hairline px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-ink transition-colors hover:text-navy"
                        >
                          Annulla
                        </button>
                      )}
                    </div>
                  </div>
                </form>
              </Reveal>
            </div>

            <div data-testid="admin-news-list" className="space-y-4">
              {newsList === null && (
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-ink">Caricamento…</p>
              )}
              {newsList !== null && newsList.length === 0 && (
                <div className="border border-hairline bg-white p-10 text-center">
                  <p className="font-serif text-xl text-navy">Nessun aggiornamento</p>
                  <p className="mt-2 text-sm text-muted-ink">Crea il primo articolo con il modulo a fianco.</p>
                </div>
              )}
              {newsList !== null &&
                newsList.map((n) => (
                  <div key={n.id} data-testid={`admin-news-item-${n.id}`} className="border border-hairline bg-white p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h4 className="font-serif text-lg font-semibold leading-snug text-navy">{n.title}</h4>
                        <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-muted-ink">
                          {new Date(n.created_at).toLocaleDateString("it-IT")} ·{" "}
                          <span className={n.published ? "text-green-700" : "text-gold-dark"}>
                            {n.published ? "Pubblicato" : "Bozza"}
                          </span>
                        </p>
                      </div>
                      <div className="flex shrink-0 gap-2">
                        <button
                          data-testid={`admin-news-edit-${n.id}`}
                          onClick={() => {
                            setNewsForm({ id: n.id, title: n.title, body: n.body, published: n.published });
                            window.scrollTo(0, 0);
                          }}
                          aria-label="Modifica"
                          className="flex h-9 w-9 items-center justify-center border border-hairline text-navy transition-colors hover:border-gold hover:text-gold-dark"
                        >
                          <Pencil size={13} strokeWidth={1.5} />
                        </button>
                        <button
                          data-testid={`admin-news-delete-${n.id}`}
                          onClick={() => deleteNews(n.id)}
                          aria-label="Elimina"
                          className="flex h-9 w-9 items-center justify-center border border-hairline text-navy transition-colors hover:border-red-300 hover:text-red-500"
                        >
                          <Trash2 size={13} strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                    <p className="mt-3 line-clamp-2 text-sm text-muted-ink">{n.body.replace(/\s+/g, " ").slice(0, 160)}…</p>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
